import type { AssetClassLevel } from '@generated/graphql';
import type { FinancialProduct } from '@interfaces/financialProduct';
import { Badge } from '@ovComponents/2-component/badge/badge';
import {
  type AssetClassColor,
  assetClassChipColor,
  useAssetClassColorRamp,
} from '@ovComponents/2-component/pieChart/getChartColors';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { Box, IconButton, Tooltip, Typography } from 'hephaestus/web';
import {
  DragIndicator as DragIndicatorIcon,
  RemoveCircle as RemoveCircleIcon,
  RemoveCircleOutlined as RemoveCircleOutlineIcon,
} from 'hephaestus/webIcons';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { NumberField } from '../numberField/numberField';
import { SecuritySelect } from '../securitySelect/securitySelect';
import {
  formatPortfolioPercentage,
  formatTargetPercentage,
  resolvePortfolioPercentage,
} from './fundTargetPercentage';
import type { DragProps, Security, SecurityTableOptions } from './types';
import {
  COLUMNS,
  GROUPED_MODE_SPACER_WIDTH,
  getItemStyle,
  TARGET_PERCENTAGE_ACTION_LANE,
} from './types';

const DRAG_HANDLE_MAX_WIDTH = '24px' as const;
const DRAG_HANDLE_COLOR = 'text.secondary' as const;
const TAX_RANK_FONT_FAMILY = 'Interphases Pro' as const;
const TAX_RANK_FONT_SIZE = '14px' as const;
const TAX_RANK_FONT_WEIGHT = 400 as const;
const TAX_RANK_HOVER_COLOR = 'primary.main' as const;
const TARGET_PERCENTAGE_MIN_WIDTH = '100px' as const;
/**
 * The column is a fifth of a table that can be as narrow as 600px, and the
 * cell padding, the action lane and the "%" adornment claim nearly all of it.
 * A field holding at most "100.00" does not need a text field's side padding,
 * and the adornment already sets the value off from the "%" on the right.
 */
const TARGET_PERCENTAGE_INPUT_SX = {
  '& .MuiOutlinedInput-root .MuiInputBase-input': {
    paddingLeft: '8px',
    paddingRight: '4px',
  },
} as const;
const TARGET_PERCENTAGE_FONT_FAMILY = 'Interphases Pro' as const;
const TARGET_PERCENTAGE_FONT_SIZE = '14px' as const;
const TARGET_PERCENTAGE_FONT_WEIGHT = 400 as const;
const TARGET_PERCENTAGE_DECIMAL_PLACES = 2 as const;
const DRAG_DISABLED_ROW_HEIGHT = '50px' as const;
const TOOLTIP_PLACEMENT = 'right' as const;
const SECURITY_HOVER_COLOR = 'primary.main' as const;
const SECURITY_LABEL_SX = {
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/** Ticker first when there is one, since that is what the advisor scans for. */
export const getSecurityLabel = (
  translateBackendString: (translatedObject: unknown) => string,
  security: Security,
): string => {
  const ticker = security.financialProduct.ticker;
  const name =
    translateBackendString(security.financialProduct.translatedName) ||
    security.financialProduct.name ||
    '';

  return ticker ? `${ticker} - ${name}` : name;
};

const SecurityCell: FC<{
  security: Security;
  index: number;
  isEditing: boolean;
  dragHandleProps: DragProps['dragHandleProps'];
  onEdit: () => void;
  onUpdate: (security: FinancialProduct) => void;
  editable?: boolean;
  exceptTickers?: string[];
  securitySelectAssetClassLevel?: AssetClassLevel | string;
}> = ({
  security,
  index,
  isEditing,
  dragHandleProps,
  onEdit,
  onUpdate,
  editable = true,
  exceptTickers = [],
  securitySelectAssetClassLevel,
}) => {
  const { translateBackendString } = useLocalization();
  const label = getSecurityLabel(translateBackendString, security);

  return (
    <TableCell
      data-testid={`security-cell-${index}`}
      align='left'
      sx={{ overflow: 'hidden' }}
      onClick={
        !security.financialProduct.isCash && editable ? onEdit : undefined
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        {!security.financialProduct.isCash && editable && (
          <Box
            {...dragHandleProps}
            className='hover-element'
            sx={{ maxWidth: DRAG_HANDLE_MAX_WIDTH }}
          >
            <DragIndicatorIcon sx={{ color: DRAG_HANDLE_COLOR }} />
          </Box>
        )}
        {isEditing && !security.financialProduct.isCash && editable ? (
          <Box onClick={(e) => e.stopPropagation()} sx={{ flex: 1 }}>
            <SecuritySelect
              testId={`security-select-${index}`}
              value={security.financialProduct.id || ''}
              label=''
              setSecurity={(newSecurity) => {
                if (newSecurity && newSecurity !== 'ANY') onUpdate(newSecurity);
              }}
              exceptTickers={exceptTickers}
              size='small'
              assetClassLevel={securitySelectAssetClassLevel}
            />
          </Box>
        ) : (
          <Tooltip placement={TOOLTIP_PLACEMENT} title={label}>
            <Box
              sx={{
                ...SECURITY_LABEL_SX,
                '&:hover': {
                  color:
                    security.financialProduct.isCash || !editable
                      ? 'inherit'
                      : SECURITY_HOVER_COLOR,
                },
              }}
            >
              {label}
            </Box>
          </Tooltip>
        )}
      </Box>
    </TableCell>
  );
};

export type SecurityRowProps = {
  security: Security;
  index: number;
  isGroupedMode: boolean;
  editable: boolean;
  exceptTickers: string[];
  options?: SecurityTableOptions;
  dragProps?: DragProps;
  securityEditingIndex: number | null;
  onEditSecurity: (index: number) => void;
  onUpdateSecurity: (index: number, newSecurity: FinancialProduct) => void;
  taxRankEditingIndex: number | null;
  currentTaxRankValue: string;
  selectTaxRankRef: React.RefObject<HTMLDivElement>;
  onTaxRankFocus: (index: number) => void;
  onTaxRankBlur: (index: number) => void;
  onTaxRankKeyDown: (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  onTaxRankValueChange: (value: string) => void;
  targetPercentageEditingIndex: number | null;
  securitiesPercentage: string[];
  selectTargetPercentageRef: React.RefObject<HTMLDivElement>;
  onTargetPercentageFocus: (index: number) => void;
  onTargetPercentageKeyDown: (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  onPercentageChange: (index: number, value: string) => void;
  portfolioPercentageEditingIndex: number | null;
  securitiesPortfolioPercentage: string[];
  selectPortfolioPercentageRef: React.RefObject<HTMLDivElement>;
  onPortfolioPercentageFocus: (index: number) => void;
  onPortfolioPercentageKeyDown: (
    index: number,
    portfolioValue: string,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  onPortfolioPercentageBlur: (index: number, portfolioValue: string) => void;
  onPortfolioPercentageChange: (index: number, value: string) => void;
  onRemoveSecurity?: (index: number) => void;
  securitySelectAssetClassLevel?: AssetClassLevel | string;
  showPortfolioPercentageColumn?: boolean;
  classTargetPercentage?: number | null;
  /** Group-reconciled portfolio % from the table; used unless this row's portfolio % is being edited. */
  reconciledPortfolioPercentage?: number | null;
  allowEffectivePortfolioFallback?: boolean;
  /**
   * Registry colour for this row's asset class, so the chip matches the donut.
   * Left unset until the registry lands, which falls the chip back to neutral
   * rather than a colour it would have to swap out.
   */
  assetClassColor?: AssetClassColor;
};

export const SecurityRow: FC<SecurityRowProps> = ({
  security,
  index,
  isGroupedMode,
  editable,
  exceptTickers,
  options,
  dragProps,
  securityEditingIndex,
  onEditSecurity,
  onUpdateSecurity,
  taxRankEditingIndex,
  currentTaxRankValue,
  selectTaxRankRef,
  onTaxRankFocus,
  onTaxRankBlur,
  onTaxRankKeyDown,
  onTaxRankValueChange,
  targetPercentageEditingIndex,
  securitiesPercentage,
  selectTargetPercentageRef,
  onTargetPercentageFocus,
  onTargetPercentageKeyDown,
  onPercentageChange,
  portfolioPercentageEditingIndex,
  securitiesPortfolioPercentage,
  selectPortfolioPercentageRef,
  onPortfolioPercentageFocus,
  onPortfolioPercentageKeyDown,
  onPortfolioPercentageBlur,
  onPortfolioPercentageChange,
  onRemoveSecurity,
  securitySelectAssetClassLevel,
  showPortfolioPercentageColumn = false,
  classTargetPercentage,
  reconciledPortfolioPercentage,
  allowEffectivePortfolioFallback = true,
  assetClassColor,
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['modelPortfolios']);
  const colorRamp = useAssetClassColorRamp();
  const editingTarget = targetPercentageEditingIndex === index;
  const editingPortfolio = portfolioPercentageEditingIndex === index;
  const withinClassPercentage =
    editingTarget || editingPortfolio
      ? Number(securitiesPercentage[index])
      : security.percentage;
  const resolvedPortfolioPercentage = editingPortfolio
    ? Number(securitiesPortfolioPercentage[index])
    : reconciledPortfolioPercentage != null &&
        Number.isFinite(withinClassPercentage)
      ? reconciledPortfolioPercentage
      : resolvePortfolioPercentage(
          security,
          classTargetPercentage,
          editingTarget && !Number.isFinite(withinClassPercentage)
            ? undefined
            : withinClassPercentage,
          allowEffectivePortfolioFallback,
        );
  const displayedTargetPercentage = formatTargetPercentage(
    Number.isFinite(withinClassPercentage)
      ? withinClassPercentage
      : security.percentage,
  );
  const displayedPortfolioPercentage = formatPortfolioPercentage(
    Number.isFinite(resolvedPortfolioPercentage)
      ? resolvedPortfolioPercentage
      : null,
  );
  const assetClassLabel =
    translateBackendString(
      security.financialProduct.primaryAssetClass?.translatedName,
    ) || security.financialProduct.primaryAssetClass?.name;
  const getTaxRankDisplay = (
    isCash?: boolean,
    isGrouped?: boolean,
  ): string | number => {
    if (isCash) return '';
    return isGrouped || !editable ? security?.taxRank || '' : index + 1;
  };

  return (
    <TableRow
      ref={dragProps?.innerRef}
      {...(dragProps?.draggableProps ?? {})}
      {...(dragProps?.rowHandleProps ?? {})}
      style={
        dragProps
          ? {
              ...getItemStyle(
                dragProps.isDragging,
                dragProps.draggableProps.style,
              ),
            }
          : {
              height: DRAG_DISABLED_ROW_HEIGHT,
              minHeight: DRAG_DISABLED_ROW_HEIGHT,
              width: '100%',
            }
      }
      showHoverElements
      hover
    >
      {isGroupedMode && <TableCell sx={{ width: GROUPED_MODE_SPACER_WIDTH }} />}
      <SecurityCell
        security={security}
        index={index}
        isEditing={securityEditingIndex === index}
        dragHandleProps={dragProps?.dragHandleProps ?? undefined}
        onEdit={() => onEditSecurity(index)}
        onUpdate={(newSecurity: FinancialProduct) =>
          onUpdateSecurity(index, newSecurity)
        }
        editable={editable}
        exceptTickers={exceptTickers}
        securitySelectAssetClassLevel={securitySelectAssetClassLevel}
      />

      {!isGroupedMode && (
        <TableCell sx={{ textAlign: COLUMNS.ASSET_CLASS.align }}>
          {assetClassLabel && (
            <Badge
              data-testid={`security-asset-class-badge-${index}`}
              label={assetClassLabel}
              color={assetClassColor?.slot}
              customColor={
                assetClassColor &&
                assetClassChipColor(colorRamp, assetClassColor)
              }
            />
          )}
        </TableCell>
      )}
      {/* Tax rank: user-editable only in ASSET_CLASS grouped mode */}
      <TableCell
        sx={{ textAlign: COLUMNS.TAX_RANK.align }}
        onClick={() =>
          isGroupedMode &&
          !security.financialProduct.isCash &&
          editable &&
          onTaxRankFocus(index)
        }
      >
        {isGroupedMode &&
        taxRankEditingIndex === index &&
        !security.financialProduct.isCash &&
        editable ? (
          <Box ref={selectTaxRankRef} onClick={(e) => e.stopPropagation()}>
            <NumberField
              testId={`tax-rank-input-${index}`}
              number={currentTaxRankValue}
              setNumber={onTaxRankValueChange}
              onBlur={() => onTaxRankBlur(index)}
              InputProps={{
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                  onTaxRankKeyDown(index, e),
                onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                  e.target.select(),
              }}
              label=''
              sx={{ width: '100%' }}
              showArrows={false}
              size='small'
              unsigned
            />
          </Box>
        ) : (
          <Box
            data-testid={`security-tax-rank-${index}`}
            sx={{
              '&:hover': {
                color:
                  security.financialProduct.isCash ||
                  !editable ||
                  !isGroupedMode
                    ? 'inherit'
                    : TAX_RANK_HOVER_COLOR,
              },
              textAlign: COLUMNS.TAX_RANK.align,
              fontFamily: TAX_RANK_FONT_FAMILY,
              fontSize: TAX_RANK_FONT_SIZE,
              fontWeight: TAX_RANK_FONT_WEIGHT,
            }}
          >
            {getTaxRankDisplay(security.financialProduct.isCash, isGroupedMode)}
          </Box>
        )}
      </TableCell>
      <TableCell
        onClick={() => editable && onTargetPercentageFocus(index)}
        sx={{ minWidth: TARGET_PERCENTAGE_MIN_WIDTH }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {editingTarget && editable ? (
              <Box
                ref={selectTargetPercentageRef}
                onClick={(e) => e.stopPropagation()}
              >
                <NumberField
                  testId={`target-percentage-input-${index}`}
                  number={securitiesPercentage[index]}
                  setNumber={(value) => {
                    onPercentageChange(index, value);
                  }}
                  InputProps={{
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                      onTargetPercentageKeyDown(index, e),
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.select(),
                  }}
                  decimalPlaces={TARGET_PERCENTAGE_DECIMAL_PLACES}
                  label=''
                  trailingIcon='percent'
                  showArrows={false}
                  size='small'
                  unsigned
                  sx={TARGET_PERCENTAGE_INPUT_SX}
                />
              </Box>
            ) : (
              <Box
                data-testid={`security-target-percentage-${index}`}
                sx={{
                  '&:hover': {
                    color: editable ? 'primary.main' : 'inherit',
                  },
                  textAlign: COLUMNS.TARGET_PERCENTAGE.align,
                  fontFamily: TARGET_PERCENTAGE_FONT_FAMILY,
                  fontSize: TARGET_PERCENTAGE_FONT_SIZE,
                  fontWeight: TARGET_PERCENTAGE_FONT_WEIGHT,
                }}
              >
                {displayedTargetPercentage}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              // The remove button is not drawn on the row being edited, so the
              // lane it is held clear for is the room the number field needs.
              width:
                editingTarget && editable ? 0 : TARGET_PERCENTAGE_ACTION_LANE,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {options?.securityAllocationChart?.columns?.[0]
              ?.buttonRemoveSecurity?.enabled &&
              onRemoveSecurity &&
              targetPercentageEditingIndex !== index &&
              editable &&
              (security.isCashEquivalent ? (
                <Tooltip
                  placement={TOOLTIP_PLACEMENT}
                  slotProps={{
                    tooltip: {
                      'data-testid': `remove-security-tooltip-${index}`,
                    },
                  }}
                  title={
                    <Typography
                      variant='bodySmall'
                      component='p'
                      sx={{ color: 'white' }}
                    >
                      {t('modelPortfolios:cashEquivalent')}
                    </Typography>
                  }
                >
                  <span>
                    <IconButton
                      aria-label='remove'
                      data-testid={`remove-security-button-${index}`}
                      className='hover-element'
                      size='xsmall'
                      disabled
                    >
                      <RemoveCircleIcon fontSize='inherit' />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  placement={TOOLTIP_PLACEMENT}
                  slotProps={{
                    tooltip: {
                      'data-testid': `remove-security-tooltip-${index}`,
                    },
                  }}
                  title={
                    <Typography
                      variant='bodySmall'
                      component='p'
                      sx={{ color: 'white' }}
                    >
                      {t('modelPortfolios:remove')}
                    </Typography>
                  }
                >
                  <IconButton
                    aria-label='delete'
                    data-testid={`remove-security-button-${index}`}
                    className='hover-element'
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveSecurity(index);
                    }}
                    size='xsmall'
                  >
                    <RemoveCircleOutlineIcon fontSize='inherit' />
                  </IconButton>
                </Tooltip>
              ))}
          </Box>
        </Box>
      </TableCell>
      {showPortfolioPercentageColumn && (
        <TableCell
          onClick={() => editable && onPortfolioPercentageFocus(index)}
          sx={{ minWidth: TARGET_PERCENTAGE_MIN_WIDTH }}
        >
          {editingPortfolio && editable ? (
            <Box
              ref={selectPortfolioPercentageRef}
              onClick={(e) => e.stopPropagation()}
            >
              <NumberField
                testId={`portfolio-percentage-input-${index}`}
                number={securitiesPortfolioPercentage[index]}
                setNumber={(value) => {
                  onPortfolioPercentageChange(index, value);
                }}
                InputProps={{
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                    onPortfolioPercentageKeyDown(
                      index,
                      e.currentTarget.value,
                      e,
                    ),
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
                    onPortfolioPercentageBlur(index, e.target.value),
                  onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                    e.target.select(),
                }}
                decimalPlaces={TARGET_PERCENTAGE_DECIMAL_PLACES}
                label=''
                trailingIcon='percent'
                showArrows={false}
                size='small'
                unsigned
              />
            </Box>
          ) : (
            <Box
              data-testid={`security-portfolio-percentage-${index}`}
              sx={{
                '&:hover': {
                  color: editable ? 'primary.main' : 'inherit',
                },
                textAlign: COLUMNS.PORTFOLIO_PERCENTAGE.align,
                fontFamily: TARGET_PERCENTAGE_FONT_FAMILY,
                fontSize: TARGET_PERCENTAGE_FONT_SIZE,
                fontWeight: TARGET_PERCENTAGE_FONT_WEIGHT,
              }}
            >
              {displayedPortfolioPercentage}
            </Box>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};
